"use client";

import { useCallback, useId, useMemo, useState } from "react";
import { useNoteLabel } from "@/hooks/use-notation";
import { useAppPiano } from "@/hooks/journey/use-app-piano";
import { useFluentStreak } from "@/hooks/journey/use-fluent-streak";
import { usePracticeRun } from "@/hooks/journey/use-practice-run";
import { AUDITION_TEMPO_FRACTION } from "@/lib/learning-journey/thresholds";
import type { LearningJourney } from "@/lib/learning-journey/journey";
import {
  applicableStepIndices,
  phraseStateToStepIndex,
  reachableStepIndices,
  stepIndexToPhraseState,
  stepPosition,
} from "@/lib/learning-journey/step-mapping";
import type { Phrase, PhraseFSMSnapshot } from "@/lib/learning-journey/types";
import { noteLabellers } from "../grip";
import {
  fluencyLine,
  hesitationLine,
  isFluent,
  naturalRate,
  practiceTitle,
  readablePass,
  readyForTime,
  readyForTimeLine,
  steadyButSlowLine,
  stumbleLine,
} from "../mastery";
import { bpmAt } from "../rhythm";
import {
  accompaniedRun,
  accompanimentSchedule,
  auditionSchedule,
  buildPracticeGroups,
  groupAnnouncement,
  groupTurn,
  jointOpeningLine,
  sustainByGroup,
  turnLine,
  type GroupTurn,
  type PracticeHand,
} from "../practice";
import { FloatingHeader } from "../shell/floating-header";
import { FloatingShell } from "../shell/floating-shell";
import { MiniSteps } from "../shell/mini-steps";
import {
  ACT_FILL,
  PANEL_PADDING_X_PX,
  PANEL_WIDTH_PX,
} from "../shell/panel-metrics";
import { HandPills, type HandPillState } from "../shell/hand-pills";
import { PassButton } from "../shell/skip-hand-button";
import { handColorOf } from "../shell/practice-keyboard";
import { RunMap } from "../shell/run-map";
import {
  AuditionButton,
  CompletedFeedback,
  PASS_DOTS,
  RetrySecondaryButton,
} from "../shell/run-controls";
import { SequenceWatcher } from "../sequence-watcher";

/**
 * View for `S3`: the two hands together, without rhythm.
 *
 * ## Why this is not the single-hand run with `hand = "both"`
 *
 * It was, and it inherited an écran whose grammar is "one group, one shape,
 * one hand". Every organ of that screen has a single referent — the grips of
 * *the* hand, where *the* hand moves, the configuration *the* hand was given
 * — and none of them has one here, so they were switched off rather than
 * replaced: the opening line, the move markers and the whole link back to the
 * grip steps came out as `null`.
 *
 * What the measurement says the step is instead. Over 125 563 transitions of
 * two-hand units, **73.1%** of the work of this run exists in neither of the
 * two single-hand runs before it — so the step is not redundant, its content
 * had simply been turned off. And that content is not in the groups, it is in
 * the joins: median **5 changes of hand per run**, 46% of a run's transitions.
 *
 * > **What this step teaches is the interleaving: whose turn it is, and what
 * > the other hand is holding meanwhile.**
 *
 * ## Not playing is a positive act here
 *
 * The reading of a group is ternary, not binary — 46.0% of groups have both
 * hands striking, **39.9%** have one striking while the other *holds*, and
 * only 14.0% have one striking with the other truly idle. The line this
 * screen replaced said "Main gauche seule" on all 53.9% of them, i.e. it was
 * false on three quarters of the groups it appeared on. See `groupTurn`.
 *
 * ## Hand identity is carried by position, not by hue
 *
 * The two hand tokens are 1.83:1 apart (1.24:1 once tinted) where WCAG asks
 * 3:1 between adjacent meaningful graphics, and they collapse onto the same
 * blue-grey in deuteranopia. So the colour confirms and never carries: the
 * lane above the keys, the letter in the turn pill and the rail of the run
 * map each say the hand without it. See `HAND_LANE_Y`.
 *
 * ## This step is the **free régime** of the two-hand step
 *
 * `S3` and `S4` are one step in two régimes, and this screen is one of them;
 * `RhythmState` is the other. « Passer en rythme » crosses into the metronome
 * and « Retour » on the step in time comes back, as often as the player wants.
 * See `PhraseFSM`, « S3 and S4 are one step in two régimes ».
 *
 * It used to cross **on its own**, on three passes a reading called even, and
 * a fourth the card counted. That was the screen disappearing under the hands
 * of someone who had pressed nothing. Whether a passage is comfortable is not
 * a fact these intervals contain: the reading can see that a pass came out
 * evenly, it cannot see that the player feels ready, and how many repetitions
 * that takes is theirs.
 *
 * ## What the reading is still for
 *
 * Two things, and it is good at both. It **measures the tempo** the timed
 * régime opens at, which is the whole point of the handover — the player
 * meets the passage in time at the speed they were just playing it. And it
 * **says what it sees**: the passes are coming out evenly, and here is the
 * tempo that would follow from them.
 *
 * It reads that off the *shape* of the passes, not off their accuracy. The
 * screen shows the player each attack and waits for it, so being accurate
 * here costs only patience. What separates « I found the notes » from « I
 * have them » is **hesitation**: the holes where the hand stops to look. See
 * `isFluent`, and `naturalRate` for the tempo, which has a veto of its own —
 * a player perfectly even at a pace no rung can match is told nothing about
 * time, because there is nothing true to tell them.
 *
 * What the reading now decides is which of the two acts the card puts
 * forward, and whether it says why. Nothing is ever withheld: the switch is
 * drawn from the first pass.
 *
 * The two passes that cannot speak for the unit are left out of the count
 * entirely: an accompanied one, where the application played half of it, and
 * a resumed one, where the player worked three bars of four.
 */

const LEAD_BOX_H_PX = 40;

/** The order at the top of the panel. Same scale as the steps before. */
const HERO_FONT_PX = 38;


const HAND_WORD: Readonly<Record<PracticeHand, string>> = {
  L: "gauche",
  R: "droite",
};

/**
 * Spacing of what the application sounds during an accompanied pass.
 *
 * Deliberately regular, and deliberately not the real rhythm: inside the
 * no-rhythm block the player is not playing in time either, so an even
 * spacing asserts nothing the step was not already leaving unsaid. The
 * audition button is the opposite case — it is a model, so it keeps the
 * score's timing.
 */
const ACCOMP_STEP_MS = 260;
const ACCOMP_HOLD_MS = 420;

/** Thresholds past which the accompanied pass is offered once. */
const OFFER_WRONG_GROUPS = 3;
const OFFER_RETRIES = 2;

type BothHandsStateProps = {
  readonly phrase: Phrase;
  readonly phraseSnapshot: PhraseFSMSnapshot;
  readonly journey: LearningJourney;
  /** Real time of a tick, for the audition. */
  readonly tickToSeconds: (tick: number) => number;
  /** Tempo written in the song, in BPM. What the measured pace is a fraction of. */
  readonly bpm: number;
  readonly ppq: number;
  readonly phraseLabel?: string;
  /**
   * `pace` is the tempo the pass was played at, as a fraction of the written
   * one, and it is what the run in time opens at. See `naturalRate`.
   */
  readonly onCompleted: (
    outcome: "validated" | "failed",
    pace?: number,
  ) => void;
};

export function BothHandsState({
  phrase,
  phraseSnapshot,
  journey,
  tickToSeconds,
  bpm,
  ppq,
  phraseLabel,
  onCompleted,
}: BothHandsStateProps) {
  const titleId = useId();
  const toNoteLabel = useNoteLabel();
  const labels = useMemo(() => noteLabellers(toNoteLabel), [toNoteLabel]);

  const allGroups = useMemo(
    () => buildPracticeGroups(phrase, "S3"),
    [phrase],
  );

  /**
   * The hand the **application** plays, when the player has accepted the
   * accompanied pass. `null` is the ordinary run, both hands theirs.
   *
   * The player picks it, and that is a measurement rather than a preference:
   * there is an obvious accompanying hand in only 43.7% of two-hand units
   * (left harmonic + right melodic 38.1%, both harmonic 30.7%, both melodic
   * 14.1%), so the application cannot choose without being wrong the rest of
   * the time.
   */
  const [accompany, setAccompany] = useState<PracticeHand | null>(null);
  /** One pass per hand, then the scaffold is gone for good. */
  const [spent, setSpent] = useState<ReadonlySet<PracticeHand>>(
    () => new Set(),
  );

  const split = useMemo(
    () =>
      accompany === null
        ? null
        : accompaniedRun(allGroups, accompany === "L" ? "R" : "L"),
    [allGroups, accompany],
  );
  const groups = split === null ? allGroups : split.groups;
  // A hand that holds is a hand that is playing: the rail lights it, and the
  // score is what says so. See `sustainByGroup`.
  const sustaining = useMemo(
    () => sustainByGroup(phrase, groups),
    [phrase, groups],
  );
  const opening = useMemo(
    () => jointOpeningLine(phrase, allGroups, labels.pitchClass),
    [phrase, allGroups, labels],
  );

  const run = usePracticeRun(phrase, groups, labels.key);

  const app = useAppPiano();

  const auditionNotes = useMemo(
    () => auditionSchedule(phrase, tickToSeconds, AUDITION_TEMPO_FRACTION),
    [phrase, tickToSeconds],
  );
  const toggleAudition = useCallback(() => {
    if (app.playing) {
      app.stop();

      return;
    }

    app.play(auditionNotes);
  }, [app, auditionNotes]);

  // The application's share sounds as the player's keys go down, then walks
  // the groups they do not own until the next one that is theirs.
  const handleGroupConfirmed = useCallback(
    (index: number) => {
      run.onGroupConfirmed(index);

      const tail = split?.tails[index];

      if (tail !== undefined && tail.length > 0) {
        app.play(accompanimentSchedule(tail, ACCOMP_STEP_MS, ACCOMP_HOLD_MS));
      }
    },
    [run, split, app],
  );

  const startAccompanied = useCallback(
    (appHand: PracticeHand) => {
      setAccompany(appHand);
      setSpent((s) => new Set(s).add(appHand));
      run.onRetry();
    },
    [run],
  );

  const leaveAccompanied = useCallback(() => {
    setAccompany(null);
    app.stop();
    run.onRetry();
  }, [app, run]);

  // Offered on an **observed** failure of the current pass, never on the
  // texture of the music: the player does not perceive a texture condition,
  // and texture does not predict what they find hard.
  const offered =
    accompany === null &&
    run.passes === 0 &&
    (run.wrongGroupsThisPass >= OFFER_WRONG_GROUPS ||
      run.retries >= OFFER_RETRIES) &&
    (!spent.has("L") || !spent.has("R"));

  /**
   * The tempo this pass was played at, or `undefined` when the pass cannot
   * speak for the unit.
   *
   * Measured against the **whole** unit's attacks (`allGroups`), never
   * against the stretch actually walked: `naturalRate` converts a rate of
   * attacks into a fraction of the written tempo using the passage's own
   * density, and half a passage has a density of its own.
   */
  const paceOf = useCallback(
    (pass: { readonly intervals: readonly number[] } | null): number | undefined => {
      if (pass === null || accompany !== null || run.resumeIndex > 0) {
        return undefined;
      }

      return naturalRate(allGroups, bpm, ppq, pass.intervals) ?? undefined;
    },
    [accompany, run.resumeIndex, allGroups, bpm, ppq],
  );

  /**
   * The player takes the metronome.
   *
   * Two dispatches, because they are two facts: a pass happened and left a
   * tempo behind it, and the step changes régime. The first is what the FSM
   * records; the second is what the FSM crosses on. Neither one implies the
   * other any more — passes accumulate without crossing anything, and the
   * switch works before a single pass has been played.
   */
  const handleEnterTime = useCallback(() => {
    const measured = paceOf(run.lastPass);

    if (run.lastPass !== null) {
      onCompleted("validated", measured);
    }

    // Nothing else stops it: switching régime unmounts this screen, and the
    // application would keep playing the hand it was covering.
    if (accompany !== null) {
      app.stop();
    }

    journey.enterTime(measured);
  }, [accompany, app, journey, onCompleted, paceOf, run.lastPass]);

  /**
   * Whether this pass says anything about the unit at all.
   *
   * Two do not. An **accompanied** one, where the application struck half the
   * attacks on a fixed 260 ms grid, so the intervals are partly its own and
   * hesitation cannot be read from them. And a **resumed** one, where the
   * player worked three bars of four. Both are excluded from the streak and
   * from the verdict alike: what the card says about a pass has to be about a
   * pass the player made whole.
   */
  const speaks = accompany === null && run.resumeIndex === 0;

  /** Fluent passes in a row. */
  const fluent = useFluentStreak(run.lastPass, run.abandons, speaks);

  /** The tempo this pass hands over, or `undefined` when there is none. */
  const pace = paceOf(run.lastPass);

  /**
   * The passage is there **and** there is a tempo to play it at.
   *
   * The second half is not a formality: a player can be perfectly even and
   * still hold a pace no rung of the ladder can match, and meeting a
   * metronome there is how it becomes a wall. `naturalRate` says so by
   * answering nothing, and the card then says nothing about time either.
   *
   * This used to fire an effect that crossed the unit into time on its own —
   * the screen disappearing under the hands of someone who had pressed
   * nothing, on the third pass a reading called even. It now moves the card's
   * primary action onto the switch and puts a sentence under it, which is the
   * whole of what a reading is entitled to do. See `mastery.ts`, « Who
   * decides *when* ».
   */
  const ready = readyForTime(fluent) && pace !== undefined;

  /**
   * Whether the reading can have an opinion on the pass that just happened.
   *
   * Under `MIN_SCORED_GROUPS` attacks there is no median to measure
   * hesitation against, so `isFluent` answers `false` for want of evidence.
   * That is not the same as « the passage is not there », and the card must
   * not treat it as such.
   */
  const readable = run.lastPass !== null && readablePass(run.lastPass);

  /**
   * Which of the two acts the card puts forward.
   *
   * The reading leads when it has one, and the pass count only leads when it
   * cannot. It used to lead unconditionally past `PASS_DOTS`, which meant
   * four passes of hunting for keys promoted « Passer en rythme » to the
   * filled button, on a passage the reading had just refused three times, and
   * the card fell silent about why. A dosage is how many repetitions a short
   * unit is worth; it is not evidence about this one.
   *
   * Nothing is withheld either way: the switch sits above the panel from the
   * first pass, and the secondary button is the same act.
   */
  const leadsToTime = ready || (!readable && run.passes >= PASS_DOTS);

  /**
   * One line under the headline, in the order the player needs it.
   *
   * The tempo they are about to meet first, when the passage is there, since
   * it is the one thing on the card they could not have known. Then, on a
   * pass that came out evenly, either what is left of the streak or the
   * reason nothing is being said about time (`steadyButSlowLine`). Then, on a
   * pass that did not, the holes — which is what this step is about — and
   * failing that the places that took several tries.
   */
  const verdict: string | null =
    run.lastPass === null
      ? null
      : ready
        ? readyForTimeLine(pace === undefined ? null : bpmAt(bpm, pace))
        : isFluent(run.lastPass)
          ? pace === undefined
            ? steadyButSlowLine()
            : fluencyLine(fluent)
          : (hesitationLine(run.lastPass) ?? stumbleLine(run.lastPass));

  const stepIndex = phraseStateToStepIndex(phraseSnapshot.state);
  // What this unit's stepper counts: the steps it actually has. A
  // right-hand-only unit has three, not five with two dead slots.
  const steps = useMemo(
    () => applicableStepIndices(phrase, phraseSnapshot.chains),
    [phrase, phraseSnapshot.chains],
  );
  const reachable = useMemo(
    () =>
      reachableStepIndices(
        phrase,
        phraseSnapshot.maxState,
        phraseSnapshot.chains,
      ),
    [phrase, phraseSnapshot.maxState, phraseSnapshot.chains],
  );

  const handleJump = useCallback(
    (idx: number) => {
      const target = stepIndexToPhraseState(idx);

      if (target === null) {
        return;
      }

      journey.revisitPhraseState(target);
    },
    [journey],
  );

  const handleBack = useCallback(() => {
    journey.stepBack();
  }, [journey]);

  const turn: GroupTurn | null = run.currentGroup
    ? groupTurn(run.currentGroup, run.held)
    : null;

  // The joint opening line is true at the first group and stale afterwards,
  // exactly like the single-hand one: it names where the hands *started*.
  const lead =
    accompany !== null
      ? `L'application joue la main ${HAND_WORD[accompany]}. Joue l'autre, à ton rythme.`
      : run.currentIndex === 0 && opening !== null
        ? opening
        : turn !== null
          ? turnLine(turn)
          : null;

  /**
   * Two heights, as in the single-hand practice and the run in time: a panel
   * while the player reads, a line across the roll once they are playing.
   *
   * The hand pills come with it. They are what this step has to say that the
   * others do not — one hand strikes while the other *holds* in 39.9% of
   * groups — and dropping them into the band is what keeps the reduction
   * from costing the step its subject.
   */
  // Outside the two shells: a band that replaced the panel would otherwise
  // unmount the thing reading the keys. See the single-hand practice.
  const announcement = (
    <div aria-live="polite" className="sr-only">
      {run.completed
        ? `Séquence terminée, passage ${run.passes}.`
        : run.currentGroup
          ? groupAnnouncement(
              run.currentGroup,
              run.held,
              run.position,
              run.total,
              labels.key,
              accompany ?? undefined,
            )
          : ""}
    </div>
  );

  const watcher = (
    <SequenceWatcher
      key={run.runId}
      groups={run.sequenceGroups}
      startIndex={run.resumeIndex}
      onGroupConfirmed={handleGroupConfirmed}
      onSequenceCompleted={run.onSequenceCompleted}
      onError={run.onError}
      onCurrentIndexChange={run.onCurrentIndexChange}
    />
  );

  if (!run.completed && run.currentIndex > 0) {
    return (
      <>
        {watcher}
        {announcement}
      <FloatingShell
        variant="band"
        ariaLabel={
          phraseLabel === undefined || phraseLabel === ""
            ? "Pratique mains ensemble"
            : `Pratique mains ensemble, ${phraseLabel}`
        }
        titleId={titleId}
        contentKey="both-hands-band"
      >
        <div
          className="flex items-center"
          style={{ gap: 12, padding: `10px ${PANEL_PADDING_X_PX}px` }}
        >
          <div
            className="flex items-center"
            style={{ gap: 16, flex: "1 1 0", minWidth: 0 }}
          >
            <span
              id={titleId}
              className="font-mono shrink-0"
              style={{
                fontSize: 11,
                letterSpacing: "0.12em",
                color: "var(--color-text-secondary)",
              }}
            >
              {accompany === null ? "MAINS ENSEMBLE" : "ACCOMPAGNÉ"}
            </span>

            {turn !== null && accompany === null && (
              <HandPills stateOf={pillStateOf(turn)} marginBottom={0} />
            )}

            {/* See the single-hand practice: the line repeated what the panel
                had just said, and starting over had nowhere to live. */}
            <RetrySecondaryButton onRetry={run.onRetry} compact />
          </div>

          {/*
            The keys to play, named. The roll shows where they are and which
            finger takes them, never what they are called — and here they
            carry their hand's colour, which is the other half of the
            question this step asks.
          */}
          <div
            className="flex items-baseline justify-center shrink-0"
            style={{ gap: 10 }}
          >
            {(run.currentGroup?.notes ?? []).map((n) => (
              <span
                key={`${n.hand}-${n.midi}`}
                className="font-mono"
                style={{
                  fontSize: 21,
                  lineHeight: 1,
                  letterSpacing: "0.02em",
                  color: handColorOf(n.hand),
                }}
              >
                {labels.key(n.midi)}
              </span>
            ))}
          </div>

          <div
            className="flex items-center justify-end"
            style={{ gap: 16, flex: "1 1 0", minWidth: 0 }}
          >
            {run.total > 0 && (
              <span
                className="font-mono tabular-nums shrink-0"
                style={{ fontSize: 11, color: "var(--color-text-secondary)" }}
              >
                {run.position} / {run.total}
              </span>
            )}

            {phraseLabel !== undefined && phraseLabel !== "" && (
              <span
                className="font-mono shrink-0"
                style={{ fontSize: 11, color: "var(--color-text-muted)" }}
              >
                {phraseLabel}
              </span>
            )}
          </div>
        </div>
      </FloatingShell>
      </>
    );
  }

  return (
    <>
      {watcher}
      {announcement}
    <FloatingShell
      variant="floating"
      width={PANEL_WIDTH_PX}
      ariaLabel={
        phraseLabel === undefined || phraseLabel === ""
          ? "Pratique mains ensemble"
          : `Pratique mains ensemble, ${phraseLabel}`
      }
      titleId={titleId}
    >
      <FloatingHeader
        stepIndex={stepPosition(steps, stepIndex)}
        stepTotal={steps.length}
        // The hands are said once, above the order, as on the steps before.
        kicker={accompany === null ? "PRATIQUE" : "PRATIQUE · ACCOMPAGNÉ"}
        titleId={titleId}
      />

      <div style={{ padding: `26px ${PANEL_PADDING_X_PX}px 20px` }}>
        {/*
          The instruction belongs to a pass that is still to come. Kept
          mounted at the end, it reserved `LEAD_BOX_H_PX` of blank above the
          card that closes the run.
        */}
        {!run.completed && (
          <>
            <p
              className="font-mono"
              style={{
                margin: 0,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: "var(--color-text-secondary)",
              }}
            >
              Mains ensemble
            </p>
            <p
              style={{
                margin: "10px 0 0",
                fontSize: HERO_FONT_PX,
                lineHeight: 1.08,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: "var(--color-text-primary)",
              }}
            >
              À toi de jouer
            </p>
          </>
        )}

        {!run.completed && (
          <div style={{ minHeight: LEAD_BOX_H_PX, marginTop: 10 }}>
            <p
              style={{
                fontSize: 15,
                lineHeight: 1.5,
                color: "var(--color-text-secondary)",
                textWrap: "pretty",
                margin: 0,
              }}
            >
              {lead}
            </p>
          </div>
        )}

        {!run.completed && (
          <div
            aria-live="polite"
            style={{
              fontSize: 12,
              lineHeight: 1.4,
              color: "var(--color-text-secondary)",
              textWrap: "pretty",
            }}
          >
            {run.diagnosis}
          </div>
        )}

        <RunMap
          groups={groups}
          currentIndex={run.currentIndex}
          sustaining={sustaining}
          hands={
            accompany === null
              ? (["R", "L"] as const)
              : accompany === "R"
                ? (["L"] as const)
                : (["R"] as const)
          }
        />

        {!run.completed && accompany === null && run.currentIndex === 0 && (
          <div
            className="flex items-center"
            style={{
              marginTop: 8,
              marginLeft: -12,
              marginRight: -12,
              justifyContent: "space-between",
            }}
          >
            {run.passes === 0 ? (
              <AuditionButton
                playing={app.playing}
                onToggle={toggleAudition}
              />
            ) : (
              <span />
            )}

            {/*
              The way into the metronome, named. It replaces a « Libre / En
              rythme » switch; the way back out is « Retour » on the step in
              time. Hidden during an accompanied pass, where half the passage
              is not the player's and « in time? » has no answer.
            */}
            {phraseSnapshot.canEnterTime && (
              <PassButton label="Passer en rythme" onClick={handleEnterTime} />
            )}
          </div>
        )}

        {!run.completed && offered && (
          <AccompanyOffer spent={spent} onChoose={startAccompanied} />
        )}

        {!run.completed && accompany !== null && (
          <button
            type="button"
            onClick={leaveAccompanied}
            className="w-full transition-colors hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
            style={{
              padding: "4px 2px",
              marginBottom: 6,
              borderRadius: 4,
              background: "transparent",
              border: "none",
              color: "var(--color-text-secondary)",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Reprendre les deux mains
          </button>
        )}

        {run.completed && (
          <CompletedFeedback
            passes={run.passes}
            onRetry={run.onRetry}
            onContinue={handleEnterTime}
            handColor={ACT_FILL}
            // The run map stays on screen above: it is the evidence, this is
            // the verdict, and the card no longer carries a box of its own.
            divided
            // « Continuer » is the word the eight other screens use for
            // « the next step ». What it does here is change the régime of
            // this one, and a control that takes a metronome has to say so.
            continueLabel="Passer en rythme →"
            ready={leadsToTime}
            // « Passage réussi » on a pass spent hunting for keys was a
            // tautology dressed as praise: the watcher waits for the right
            // key, so every pass that ends walked every attack. What the
            // headline says now is which of the two things happened.
            title={speaks ? practiceTitle(run.lastPass) : undefined}
            note={speaks ? (verdict ?? undefined) : undefined}
            audition={
              <AuditionButton
                playing={app.playing}
                onToggle={toggleAudition}
              />
            }
          />
        )}

      </div>

      <MiniSteps
        active={stepIndex}
        steps={steps}
        reachable={reachable}
        onJump={handleJump}
        onBack={phraseSnapshot.canStepBack ? handleBack : undefined}
        label={run.total > 0 ? `${run.position} / ${run.total}` : undefined}
      />
    </FloatingShell>
    </>
  );
}

// ── Whose turn it is ──────────────────────────────────────────────

/**
 * Two pills, `G` and `D`, whose fill **is** the instruction.
 *
 * Replaces a constant two-colour legend plus a sentence repeated at every
 * group. Three states, told apart without colour — filled, outlined with a
 * bar, outlined — because the two hand hues are 1.83:1 apart and a player who
 * cannot separate them still has to know who plays.
 *
 * The middle state is the one that did not exist: 39.9% of groups ask a hand
 * to *hold* while the other plays, and the screen used to tell that hand it
 * was doing nothing.
 */
/** What each hand is doing at this group, as the pills read it. */
function pillStateOf(turn: GroupTurn): (hand: PracticeHand) => HandPillState {
  return (hand) => {
    if (turn.kind === "both" || turn.playing === hand) {
      return "plays";
    }

    return turn.kind === "one-holds" ? "holds" : "waits";
  };
}

// ── The accompanied pass ──────────────────────────────────────────

/**
 * The offer, made once, in secondary, and never as a modal.
 *
 * It is a scaffold: one pass per hand and then it is gone. Left in place it
 * would become the default mode of whoever is struggling, which is the
 * opposite of the point — and it must never degrade into "one hand, the other
 * silent", which is the two runs the player has already done.
 */
function AccompanyOffer({
  spent,
  onChoose,
}: {
  spent: ReadonlySet<PracticeHand>;
  onChoose: (appHand: PracticeHand) => void;
}) {
  const available = (["L", "R"] as const).filter((h) => !spent.has(h));

  if (available.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        marginBottom: 8,
        padding: "10px 12px",
        borderRadius: 8,
        border: "1px solid var(--color-border-subtle)",
      }}
    >
      <p
        style={{
          margin: "0 0 8px",
          fontSize: 12,
          lineHeight: 1.4,
          color: "var(--color-text-secondary)",
        }}
      >
        Tu veux que l&apos;application joue une main pendant que tu joues
        l&apos;autre ?
      </p>

      <div className="flex items-center" style={{ gap: 8 }}>
        {available.map((hand) => (
          <button
            key={hand}
            type="button"
            onClick={() => onChoose(hand)}
            className="transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
            style={{
              padding: "7px 12px",
              borderRadius: 8,
              border: `1px solid ${handColorOf(hand)}`,
              background: "transparent",
              color: handColorOf(hand),
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Elle joue la main {HAND_WORD[hand]}
          </button>
        ))}
      </div>
    </div>
  );
}
